import { useState, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  BarChart3, ChevronLeft, Settings, TrendingUp, TrendingDown,
  Users, Target, Loader2, Save, X, Clock, Euro,
  ChevronDown, ChevronUp, Edit2, Plus, Trash2, Wallet,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, Cell,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { MONTHS_ABBR } from "../../lib/constants";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";

const DEFAULT_CONFIG = {
  capacidad_objetivo: 300,
  capacidad_pct_objetivo: 0.90,
  leads_objetivo: 300,
  altas_objetivo: 60,
  tasa_cierre_objetivo: 0.40,
  tasa_adquis_objetivo: 0.05,
  bajas_objetivo: 50,
  churn_objetivo: 0.01,
  ticket_medio_objetivo: 80,
  permanencia_media_objetivo: 36,
  categorias_ingresos: [
    { name: "EFECTIVO", iva: 21 },
    { name: "BANCO", iva: 21 },
    { name: "FISIOTERAPIA", iva: 0 },
  ],
  categorias_gastos: [
    { name: "PERSONAL", iva: 0 },
    { name: "ALQUILER", iva: 21 },
    { name: "SUMINISTROS", iva: 21 },
    { name: "MARKETING", iva: 21 },
    { name: "OTROS", iva: 21 },
  ],
};

function KPICard({ label, value, objetivo, format = "number", icon: Icon, inverse = false, subline, currentMonthValue }) {
  const formatValue = (v) => {
    if (format === "percent") return `${(v * 100).toFixed(1)}%`;
    if (format === "currency") return `${v.toFixed(2)}€`;
    if (format === "months") return `${v.toFixed(1)} meses`;
    return v.toFixed(0);
  };

  const pctOfGoal = objetivo > 0 ? (value / objetivo) * 100 : 0;
  const isGood = inverse ? pctOfGoal <= 100 : pctOfGoal >= 100;
  const showCurrentMonth = currentMonthValue !== undefined && currentMonthValue !== null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon size={16} className="text-gray-400" />}
      </div>
      <div className="flex items-end gap-2 mb-1">
        <span className="text-2xl font-bold text-gray-900">{formatValue(value)}</span>
      </div>
      {objetivo > 0 && (
        <>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-400">
              Obj: {formatValue(objetivo)}
              {showCurrentMonth && ` · Mes: ${formatValue(currentMonthValue)}`}
              {subline ? ` · ${subline}` : ""}
            </span>
            <span className={`font-medium ${isGood ? "text-emerald-600" : "text-amber-600"}`}>
              {pctOfGoal.toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${isGood ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${Math.min(pctOfGoal, 100)}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function normalizeCategorias(cats) {
  if (!cats || !Array.isArray(cats)) return [];
  return cats.map(c => typeof c === "string" ? { name: c, iva: 21 } : c);
}

function catName(cat) {
  return typeof cat === "string" ? cat : cat.name;
}

function GlobalMetric({ label, value, icon: Icon, tooltip }) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleOpen = () => {
    if (!tooltip) return;
    setOpen(true);
    requestAnimationFrame(() => setVisible(true));
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setOpen(false), 200);
  };

  const content = (
    <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon size={20} className="text-brand" />
          <span className="font-semibold text-gray-800">{label}</span>
        </div>
        <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-brand-light rounded-xl">
          <Icon size={22} className="text-brand" />
          <span className="text-xl font-bold text-brand">{value}</span>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">{tooltip}</p>
      </div>
      <div className="px-6 py-4 border-t border-gray-100">
        <button
          onClick={handleClose}
          className="w-full px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
        >
          Cerrar
        </button>
      </div>
    </>
  );

  const drawer = open ? createPortal(
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={handleClose}
      />
      <div className={`
        hidden md:flex fixed top-0 right-0 h-full w-full max-w-sm
        flex-col bg-white shadow-2xl
        transition-transform duration-200 ease-out
        ${visible ? "translate-x-0" : "translate-x-full"}
      `}>
        {content}
      </div>
      <div className={`
        md:hidden fixed bottom-0 left-0 right-0 rounded-t-2xl
        bg-white shadow-2xl
        transition-transform duration-200 ease-out
        ${visible ? "translate-y-0" : "translate-y-full"}
        flex flex-col max-h-[70vh]
      `}>
        {content}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div
        className="flex items-center gap-2 px-4 py-2 bg-brand-light rounded-lg cursor-pointer"
        onClick={handleOpen}
      >
        <Icon size={18} className="text-brand" />
        <div>
          <span className="text-xs text-gray-500 block">{label}</span>
          <span className="font-bold text-brand">{value}</span>
        </div>
      </div>
      {drawer}
    </>
  );
}

function getReplicatedValue(finances, year, month, tipo, categoria) {
  for (let m = month; m >= 1; m--) {
    const found = finances.find(
      (f) => f.year === year && f.month === m && f.tipo === tipo && f.categoria === categoria
    );
    if (found && found.importe !== null && found.importe !== undefined) {
      return { value: found.importe, isReplicated: m !== month, sourceMonth: m };
    }
  }
  return { value: 0, isReplicated: false, sourceMonth: null };
}

function FinancesPanel({ year, config, finances, onSave, onClose, saving }) {
  const [visible, setVisible] = useState(false);
  const [localFinances, setLocalFinances] = useState({});
  const [newCategoria, setNewCategoria] = useState({ tipo: null, nombre: "", iva: 21 });
  const [categoriasIngresos, setCategoriasIngresos] = useState(
    normalizeCategorias(config.categorias_ingresos || DEFAULT_CONFIG.categorias_ingresos)
  );
  const [categoriasGastos, setCategoriasGastos] = useState(
    normalizeCategorias(config.categorias_gastos || DEFAULT_CONFIG.categorias_gastos)
  );

  useEffect(() => {
    const financesMap = {};
    finances?.forEach((f) => {
      if (f.importe !== null && f.importe !== undefined) {
        const key = `${f.tipo}-${f.categoria}-${f.month}`;
        financesMap[key] = f.importe;
      }
    });
    setLocalFinances(financesMap);
  }, [finances]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const getDisplayValue = useCallback((month, tipo, categoria) => {
    for (let m = month; m >= 1; m--) {
      const localKey = `${tipo}-${categoria}-${m}`;
      if (localFinances[localKey] !== undefined && localFinances[localKey] !== "") {
        return { 
          value: localFinances[localKey], 
          isReplicated: m !== month,
          sourceMonth: m 
        };
      }
      const found = finances.find(
        (f) => f.year === year && f.month === m && f.tipo === tipo && f.categoria === categoria
      );
      if (found && found.importe !== null && found.importe !== undefined) {
        return { value: found.importe, isReplicated: m !== month, sourceMonth: m };
      }
    }
    return { value: 0, isReplicated: false, sourceMonth: null };
  }, [finances, year, localFinances]);

  const handleCellChange = (month, tipo, categoria, value) => {
    const localKey = `${tipo}-${categoria}-${month}`;
    setLocalFinances((prev) => ({ ...prev, [localKey]: value }));
  };

  const calculateTotals = useCallback((tipo, categorias) => {
    const monthTotals = {};
    for (let m = 1; m <= 12; m++) {
      monthTotals[m] = categorias.reduce((sum, cat) => {
        const { value } = getDisplayValue(m, tipo, catName(cat));
        return sum + (parseFloat(value) || 0);
      }, 0);
    }
    const yearTotal = Object.values(monthTotals).reduce((a, b) => a + b, 0);
    return { monthTotals, yearTotal };
  }, [getDisplayValue]);

  const ingresosTotals = useMemo(
    () => calculateTotals("ingreso", categoriasIngresos),
    [calculateTotals, categoriasIngresos]
  );
  const gastosTotals = useMemo(
    () => calculateTotals("gasto", categoriasGastos),
    [calculateTotals, categoriasGastos]
  );

  const handleAddCategoria = (tipo) => {
    if (!newCategoria.nombre.trim()) return;
    const nombre = newCategoria.nombre.trim().toUpperCase();
    if (tipo === "ingreso") {
      if (!categoriasIngresos.some(c => catName(c) === nombre)) {
        setCategoriasIngresos([...categoriasIngresos, { name: nombre, iva: newCategoria.iva ?? 21 }]);
      }
    } else {
      if (!categoriasGastos.some(c => catName(c) === nombre)) {
        setCategoriasGastos([...categoriasGastos, { name: nombre, iva: newCategoria.iva ?? 21 }]);
      }
    }
    setNewCategoria({ tipo: null, nombre: "", iva: 21 });
  };

  const handleRemoveCategoria = (tipo, categoria) => {
    if (tipo === "ingreso") {
      setCategoriasIngresos(categoriasIngresos.filter((c) => catName(c) !== catName(categoria)));
    } else {
      setCategoriasGastos(categoriasGastos.filter((c) => catName(c) !== catName(categoria)));
    }
  };

  const handleSave = () => {
    const financesToSave = [];
    
    Object.entries(localFinances).forEach(([key, value]) => {
      const [tipo, categoria, monthStr] = key.split("-");
      const month = parseInt(monthStr);
      if (value !== "" && value !== null && value !== undefined) {
        financesToSave.push({
          year,
          month,
          tipo,
          categoria,
          importe: parseFloat(value) || 0,
        });
      }
    });

    onSave({
      finances: financesToSave,
      categorias: {
        ingresos: categoriasIngresos,
        gastos: categoriasGastos,
      },
    });
  };

  const inputClass = "w-full px-1.5 py-1 text-xs text-right border border-transparent rounded focus:border-brand focus:ring-1 focus:ring-brand/20 outline-none bg-transparent hover:bg-gray-50 focus:bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

  const renderTable = (tipo, categorias, colorClass, totals) => {
    const isIngreso = tipo === "ingreso";
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-sm font-semibold ${colorClass}`}>
            {isIngreso ? "Ingresos" : "Gastos"}
          </h4>
          <button
            onClick={() => setNewCategoria({ tipo, nombre: "", iva: 21 })}
            className="text-xs text-gray-500 hover:text-brand flex items-center gap-1"
          >
            <Plus size={12} /> Categoría
          </button>
        </div>

        {newCategoria.tipo === tipo && (
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newCategoria.nombre}
              onChange={(e) => setNewCategoria({ ...newCategoria, nombre: e.target.value })}
              placeholder="Nombre categoría"
              className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded"
              onKeyDown={(e) => e.key === "Enter" && handleAddCategoria(tipo)}
              autoFocus
            />
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">IVA</span>
              <input
                type="text"
                inputMode="numeric"
                value={newCategoria.iva}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, "");
                  setNewCategoria({ ...newCategoria, iva: raw === "" ? "" : Math.min(100, parseInt(raw)) });
                }}
                className="w-10 px-1 py-1 text-xs border border-gray-200 rounded text-center"
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
            <button
              onClick={() => handleAddCategoria(tipo)}
              className="px-2 py-1 text-xs bg-brand text-white rounded"
            >
              Añadir
            </button>
            <button
              onClick={() => setNewCategoria({ tipo: null, nombre: "", iva: 21 })}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-xs min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-center py-2 px-1 font-medium text-gray-500 w-12">%IVA</th>
                <th className="text-left py-2 pr-2 font-medium text-gray-500 w-24">Categoría</th>
                {MONTHS_ABBR.map((m, i) => (
                  <th key={i} className="text-center py-2 px-1 font-medium text-gray-400 w-14">{m}</th>
                ))}
                <th className="text-right py-2 pl-2 font-semibold text-gray-600 w-16">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {categorias.map((cat) => {
                const name = catName(cat);
                const rowTotal = Array.from({ length: 12 }, (_, i) => {
                  const { value } = getDisplayValue(i + 1, tipo, name);
                  return parseFloat(value) || 0;
                }).reduce((a, b) => a + b, 0);

                return (
                  <tr key={name} className="border-b border-gray-50 group">
                    <td className="py-1 px-1 text-center">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={cat.iva ?? 21}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          const iva = raw === "" ? "" : Math.min(100, parseInt(raw));
                          const setter = isIngreso ? setCategoriasIngresos : setCategoriasGastos;
                          setter(prev => prev.map(c => catName(c) === name ? { ...c, iva } : c));
                        }}
                        onBlur={() => {
                          const setter = isIngreso ? setCategoriasIngresos : setCategoriasGastos;
                          setter(prev => prev.map(c => catName(c) === name ? { ...c, iva: parseInt(c.iva) || 0 } : c));
                        }}
                        className="w-10 px-1 py-0.5 text-xs text-center border border-transparent rounded hover:border-gray-200 focus:border-brand focus:ring-1 focus:ring-brand/20 outline-none bg-transparent"
                      />
                    </td>
                    <td className="py-1 pr-2 font-medium text-gray-700 flex items-center gap-1">
                      <span className="truncate">{name}</span>
                      <button
                        onClick={() => handleRemoveCategoria(tipo, cat)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                      >
                        <Trash2 size={10} />
                      </button>
                    </td>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = i + 1;
                      const localKey = `${tipo}-${name}-${month}`;
                      const hasLocalValue = localFinances[localKey] !== undefined;
                      const { value, isReplicated } = getDisplayValue(month, tipo, name);
                      const showAsReplicated = isReplicated && !hasLocalValue;
                      return (
                        <td key={month} className="py-1 px-0.5">
                          <input
                            type="number"
                            min="0"
                            value={hasLocalValue ? localFinances[localKey] : (showAsReplicated ? "" : value || "")}
                            onChange={(e) => handleCellChange(month, tipo, name, e.target.value)}
                            placeholder={showAsReplicated ? String(value) : ""}
                            className={`${inputClass} ${showAsReplicated ? "text-gray-400 italic placeholder:text-gray-400" : ""}`}
                          />
                        </td>
                      );
                    })}
                    <td className={`py-1 pl-2 text-right font-semibold ${colorClass}`}>
                      {rowTotal.toLocaleString("es-ES")}
                    </td>
                  </tr>
                );
              })}
              <tr className={`${isIngreso ? "bg-emerald-50" : "bg-red-50"} font-semibold`}>
                <td />
                <td className={`py-2 pr-2 ${colorClass}`}>TOTAL</td>
                {Array.from({ length: 12 }, (_, i) => (
                  <td key={i + 1} className={`py-2 px-1 text-center ${colorClass}`}>
                    {totals.monthTotals[i + 1]?.toLocaleString("es-ES") || 0}
                  </td>
                ))}
                <td className={`py-2 pl-2 text-right ${colorClass}`}>
                  {totals.yearTotal.toLocaleString("es-ES")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const formContent = (
    <div className="space-y-6">
      <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
        <strong>Replicación automática:</strong> Si introduces un valor en un mes, se replica automáticamente 
        a los meses siguientes (en gris). Para cambiar, edita el mes específico.
      </div>

      {renderTable("ingreso", categoriasIngresos, "text-emerald-600", ingresosTotals)}
      {renderTable("gasto", categoriasGastos, "text-red-600", gastosTotals)}

      <div className="p-4 bg-gray-50 rounded-xl">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Resultado Mensual</h4>
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-xs min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-2 font-medium text-gray-500 w-24"></th>
                {MONTHS_ABBR.map((m, i) => (
                  <th key={i} className="text-center py-2 px-1 font-medium text-gray-400 w-14">{m}</th>
                ))}
                <th className="text-right py-2 pl-2 font-semibold text-gray-600 w-16">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ivaRepByMonth = {};
                const ivaSopByMonth = {};
                let ivaRepYearTotal = 0;
                let ivaSopYearTotal = 0;
                for (let m = 1; m <= 12; m++) {
                  const monthIvaRep = categoriasIngresos.reduce((sum, cat) => {
                    const { value } = getDisplayValue(m, "ingreso", catName(cat));
                    return sum + ((parseFloat(value) || 0) * (cat.iva ?? 21) / 100);
                  }, 0);
                  const monthIvaSop = categoriasGastos.reduce((sum, cat) => {
                    const { value } = getDisplayValue(m, "gasto", catName(cat));
                    return sum + ((parseFloat(value) || 0) * (cat.iva ?? 21) / 100);
                  }, 0);
                  ivaRepByMonth[m] = monthIvaRep;
                  ivaSopByMonth[m] = monthIvaSop;
                  ivaRepYearTotal += monthIvaRep;
                  ivaSopYearTotal += monthIvaSop;
                }
                return (
                  <>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-emerald-600">Ingresos</td>
                      {Array.from({ length: 12 }, (_, i) => (
                        <td key={i + 1} className="py-2 px-1 text-center text-emerald-600">
                          {(ingresosTotals.monthTotals[i + 1] || 0).toLocaleString("es-ES")}
                        </td>
                      ))}
                      <td className="py-2 pl-2 text-right font-semibold text-emerald-600">
                        {ingresosTotals.yearTotal.toLocaleString("es-ES")}€
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-amber-600">IVA Rep.</td>
                      {Array.from({ length: 12 }, (_, i) => (
                        <td key={i + 1} className="py-2 px-1 text-center text-amber-600">
                          {ivaRepByMonth[i + 1] > 0 ? ivaRepByMonth[i + 1].toLocaleString("es-ES", { maximumFractionDigits: 0 }) : ""}
                        </td>
                      ))}
                      <td className="py-2 pl-2 text-right font-semibold text-amber-600">
                        {ivaRepYearTotal.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-red-500">Gastos</td>
                      {Array.from({ length: 12 }, (_, i) => (
                        <td key={i + 1} className="py-2 px-1 text-center text-red-500">
                          {(gastosTotals.monthTotals[i + 1] || 0).toLocaleString("es-ES")}
                        </td>
                      ))}
                      <td className="py-2 pl-2 text-right font-semibold text-red-500">
                        {gastosTotals.yearTotal.toLocaleString("es-ES")}€
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-orange-500">IVA Sop.</td>
                      {Array.from({ length: 12 }, (_, i) => (
                        <td key={i + 1} className="py-2 px-1 text-center text-orange-500">
                          {ivaSopByMonth[i + 1] > 0 ? ivaSopByMonth[i + 1].toLocaleString("es-ES", { maximumFractionDigits: 0 }) : ""}
                        </td>
                      ))}
                      <td className="py-2 pl-2 text-right font-semibold text-orange-500">
                        {ivaSopYearTotal.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-semibold text-gray-700">Resultado</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const month = i + 1;
                        const resultado = (ingresosTotals.monthTotals[month] || 0) + ivaRepByMonth[month] - (gastosTotals.monthTotals[month] || 0) - ivaSopByMonth[month];
                        return (
                          <td key={month} className={`py-2 px-1 text-center font-semibold ${resultado >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {resultado.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                          </td>
                        );
                      })}
                      {(() => {
                        const yearResultado = ingresosTotals.yearTotal + ivaRepYearTotal - gastosTotals.yearTotal - ivaSopYearTotal;
                        return (
                          <td className={`py-2 pl-2 text-right font-bold ${yearResultado >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {yearResultado.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€
                          </td>
                        );
                      })()}
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={close}
      />

      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-4xl
          flex-col bg-white shadow-2xl
          transition-transform duration-250 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <Wallet size={20} className="text-purple-600" />
            </div>
            <div>
              <span className="text-base font-semibold text-gray-900">Datos Mensuales {year}</span>
              <p className="text-xs text-gray-500">Clientes, ingresos y gastos por categoría</p>
            </div>
          </div>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 px-6 py-6 overflow-y-auto">
          {formContent}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={close}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </div>

      <div
        className={`
          md:hidden fixed inset-0 bg-white
          transition-transform duration-250 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
          flex flex-col
        `}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <Wallet size={16} className="text-purple-600" />
            </div>
            <span className="text-base font-semibold text-gray-900">Datos {year}</span>
          </div>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 px-5 py-4 overflow-y-auto">
          {formContent}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={close}
            className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

function ConfigModal({ config, onSave, onClose, saving }) {
  const [form, setForm] = useState(config);
  const [visible, setVisible] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none";

  const formContent = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Capacidad</label>
          <input
            type="number"
            value={form.capacidad_objetivo || ""}
            onChange={(e) => update("capacidad_objetivo", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">% Capacidad Obj.</label>
          <input
            type="number"
            value={((form.capacidad_pct_objetivo || 0) * 100).toFixed(0)}
            onChange={(e) => update("capacidad_pct_objetivo", (parseInt(e.target.value) || 0) / 100)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Leads/año</label>
          <input
            type="number"
            value={form.leads_objetivo || ""}
            onChange={(e) => update("leads_objetivo", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Altas/año</label>
          <input
            type="number"
            value={form.altas_objetivo || ""}
            onChange={(e) => update("altas_objetivo", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tasa Cierre %</label>
          <input
            type="number"
            value={((form.tasa_cierre_objetivo || 0) * 100).toFixed(0)}
            onChange={(e) => update("tasa_cierre_objetivo", (parseInt(e.target.value) || 0) / 100)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tasa Adquis. %</label>
          <input
            type="number"
            step="0.1"
            value={((form.tasa_adquis_objetivo || 0) * 100).toFixed(1)}
            onChange={(e) => update("tasa_adquis_objetivo", (parseFloat(e.target.value) || 0) / 100)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Bajas/año</label>
          <input
            type="number"
            value={form.bajas_objetivo || ""}
            onChange={(e) => update("bajas_objetivo", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Churn %</label>
          <input
            type="number"
            step="0.1"
            value={((form.churn_objetivo || 0) * 100).toFixed(1)}
            onChange={(e) => update("churn_objetivo", (parseFloat(e.target.value) || 0) / 100)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ticket Medio (€)</label>
          <input
            type="number"
            step="0.01"
            value={form.ticket_medio_objetivo || ""}
            onChange={(e) => update("ticket_medio_objetivo", parseFloat(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Permanencia (meses)</label>
          <input
            type="number"
            value={form.permanencia_media_objetivo || ""}
            onChange={(e) => update("permanencia_media_objetivo", parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={close}
      />

      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl
          transition-transform duration-250 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="text-base font-semibold text-gray-900">Configuración de Objetivos</span>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 px-6 py-6 overflow-y-auto">
          {formContent}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={close}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </div>

      <div
        className={`
          md:hidden fixed inset-0 bg-white
          transition-transform duration-250 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
          flex flex-col
        `}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-base font-semibold text-gray-900">Configuración de Objetivos</span>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 px-5 py-4 overflow-y-auto">
          {formContent}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={close}
            className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

export default function Metricas({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [showConfig, setShowConfig] = useState(false);
  const [showFinances, setShowFinances] = useState(false);
  const [showFinancesPanel, setShowFinancesPanel] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: leads, loading: loadingLeads } = useQuery(async () => {
    const { data } = await supabase.from("leads").select("*").eq("student_id", user.id);
    return data || [];
  }, [user.id]);

  const { data: monthlyData, loading: loadingMonthly, refetch: refetchMonthly } = useQuery(async () => {
    const { data } = await supabase.from("monthly_data").select("*").eq("student_id", user.id).eq("year", year);
    return data || [];
  }, [user.id, year]);

  const { data: financesData, loading: loadingFinances, refetch: refetchFinances } = useQuery(async () => {
    const { data } = await supabase.from("monthly_finances").select("*").eq("student_id", user.id).eq("year", year);
    return data || [];
  }, [user.id, year]);

  const { data: configData, loading: loadingConfig, refetch: refetchConfig } = useQuery(async () => {
    const { data } = await supabase.from("metrics_config").select("*").eq("student_id", user.id).single();
    return data || DEFAULT_CONFIG;
  }, [user.id]);

  const { data: bajasData, loading: loadingBajas } = useQuery(async () => {
    const { data } = await supabase.from("bajas").select("fecha_baja").eq("student_id", user.id);
    return data || [];
  }, [user.id]);

  const config = { ...DEFAULT_CONFIG, ...configData };
  const loading = loadingLeads || loadingMonthly || loadingConfig || loadingFinances || loadingBajas;

  const getFinanceValue = useCallback((month, tipo, categoria) => {
    return getReplicatedValue(financesData || [], year, month, tipo, categoria);
  }, [financesData, year]);

  const metrics = useMemo(() => {
    if (!leads) return [];
    const categoriasIngresos = normalizeCategorias(config.categorias_ingresos || DEFAULT_CONFIG.categorias_ingresos);
    const categoriasGastos = normalizeCategorias(config.categorias_gastos || DEFAULT_CONFIG.categorias_gastos);

    return MONTHS_ABBR.map((_, i) => {
      const month = i + 1;
      const monthData = monthlyData?.find((d) => d.month === month) || {};

      const leadsInMonth = leads.filter((l) => {
        const d = new Date(l.fecha_contacto);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      });

      const altasInMonth = leads.filter((l) => {
        if (l.resultado !== "cliente" || !l.fecha_resultado) return false;
        const d = new Date(l.fecha_resultado);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      });

      const listaEsperaInMonth = leadsInMonth.filter((l) => l.resultado === "lista_espera");

      const bajasRegisInMonth = bajasData?.filter((b) => {
        const d = new Date(b.fecha_baja);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      }).length || 0;

      let n_clientes = monthData.n_clientes;
      if (n_clientes === undefined || n_clientes === null) {
        for (let m = month - 1; m >= 1; m--) {
          const prev = monthlyData?.find((d) => d.month === m);
          if (prev?.n_clientes !== undefined && prev.n_clientes !== null) {
            n_clientes = prev.n_clientes;
            break;
          }
        }
      }
      n_clientes = n_clientes || 0;

      const capacidad = monthData.capacidad || config.capacidad_objetivo || 300;
      const lista_espera = monthData.lista_espera || listaEsperaInMonth.length;
      const ticket_medio = monthData.ticket_medio || 0;

      let ingresos = 0;
      let iva = 0;
      categoriasIngresos.forEach((cat) => {
        const { value } = getFinanceValue(month, "ingreso", catName(cat));
        const amount = parseFloat(value) || 0;
        ingresos += amount;
        iva += amount * (cat.iva ?? 21) / 100;
      });

      let gastos = 0;
      let ivaSoportado = 0;
      categoriasGastos.forEach((cat) => {
        const { value } = getFinanceValue(month, "gasto", catName(cat));
        const amount = parseFloat(value) || 0;
        gastos += amount;
        ivaSoportado += amount * (cat.iva ?? 21) / 100;
      });

      const totalLeads = leadsInMonth.length;
      const totalAltas = altasInMonth.length;
      const tasaCierre = totalLeads > 0 ? totalAltas / totalLeads : 0;

      let prevClientes = 0;
      for (let m = month - 1; m >= 1; m--) {
        const prev = monthlyData?.find((d) => d.month === m);
        if (prev?.n_clientes != null) {
          prevClientes = prev.n_clientes;
          break;
        }
      }

      const tasaAdquis = prevClientes > 0 ? totalAltas / prevClientes : 0;
      
      const bajas = month === 1 
        ? 0 
        : Math.max(0, (prevClientes + totalAltas) - n_clientes);
      const churnRate = n_clientes > 0 ? bajas / n_clientes : 0;
      const pctCapacidad = capacidad > 0 ? n_clientes / capacidad : 0;

      return {
        month,
        label: MONTHS_ABBR[i],
        n_clientes,
        capacidad,
        pct_capacidad: pctCapacidad,
        leads: totalLeads,
        altas: totalAltas,
        lista_espera,
        bajas,
        bajas_regis: bajasRegisInMonth,
        tasa_cierre: tasaCierre,
        tasa_adquis: tasaAdquis,
        churn_rate: churnRate,
        ticket_medio,
        ingresos,
        iva,
        iva_soportado: ivaSoportado,
        gastos,
        resultado: ingresos + iva - gastos - ivaSoportado,
      };
    });
  }, [leads, monthlyData, bajasData, year, config, getFinanceValue]);

  const totals = useMemo(() => {
    const totalLeads = metrics.reduce((s, m) => s + m.leads, 0);
    const totalAltas = metrics.reduce((s, m) => s + m.altas, 0);
    const totalBajas = metrics.reduce((s, m) => s + m.bajas, 0);
    const totalBajasRegis = metrics.reduce((s, m) => s + m.bajas_regis, 0);
    const totalListaEspera = metrics.reduce((s, m) => s + m.lista_espera, 0);
    const totalIngresos = metrics.reduce((s, m) => s + m.ingresos, 0);
    const totalIva = metrics.reduce((s, m) => s + m.iva, 0);
    const totalGastos = metrics.reduce((s, m) => s + m.gastos, 0);
    const totalIvaSoportado = metrics.reduce((s, m) => s + m.iva_soportado, 0);

    const validMonths = metrics.filter((m) => m.n_clientes > 0);
    const lastMonth = validMonths[validMonths.length - 1];
    const nClientes = lastMonth?.n_clientes || 0;
    const avgTicketMedio = nClientes > 0 ? totalIngresos / nClientes / 12 : 0;

    const avgChurn = validMonths.length > 0
      ? validMonths.reduce((s, m) => s + m.churn_rate, 0) / validMonths.length
      : 0;

    const permanenciaMedia = avgChurn > 0 ? (1 / avgChurn) / 2 : 18;
    const ltv = avgTicketMedio * permanenciaMedia;

    return {
      leads: totalLeads,
      altas: totalAltas,
      bajas: totalBajas,
      bajas_regis: totalBajasRegis,
      lista_espera: totalListaEspera,
      n_clientes: lastMonth?.n_clientes || 0,
      capacidad: lastMonth?.capacidad || config.capacidad_objetivo,
      pct_capacidad: lastMonth?.pct_capacidad || 0,
      tasa_cierre: totalLeads > 0 ? totalAltas / totalLeads : 0,
      tasa_adquis: lastMonth?.tasa_adquis || 0,
      churn_rate: avgChurn,
      ticket_medio: avgTicketMedio,
      permanencia_media: permanenciaMedia,
      ltv,
      ingresos: totalIngresos,
      iva: totalIva,
      iva_soportado: totalIvaSoportado,
      gastos: totalGastos,
      resultado: totalIngresos + totalIva - totalGastos - totalIvaSoportado,
    };
  }, [metrics, config]);

  const kpiProgress = useMemo(() => {
    return [
      { label: "CAP", value: config.capacidad_objetivo > 0 ? (totals.n_clientes / config.capacidad_objetivo) * 100 : 0 },
      { label: "%CAP", value: config.capacidad_pct_objetivo > 0 ? (totals.pct_capacidad / config.capacidad_pct_objetivo) * 100 : 0 },
      { label: "LEADS", value: config.leads_objetivo > 0 ? (totals.leads / config.leads_objetivo) * 100 : 0 },
      { label: "ALTAS", value: config.altas_objetivo > 0 ? (totals.altas / config.altas_objetivo) * 100 : 0 },
      { label: "CIERRE", value: config.tasa_cierre_objetivo > 0 ? (totals.tasa_cierre / config.tasa_cierre_objetivo) * 100 : 0 },
      { label: "BAJAS", value: config.bajas_objetivo > 0 ? ((config.bajas_objetivo - totals.bajas) / config.bajas_objetivo) * 100 + 100 : 100 },
      { label: "CHURN", value: config.churn_objetivo > 0 ? ((config.churn_objetivo - totals.churn_rate) / config.churn_objetivo) * 100 + 100 : 100 },
    ];
  }, [totals, config]);

  // Métricas del mes actual: solo se muestran cuando el año seleccionado es el actual.
  const currentMonthMetric = useMemo(() => {
    if (year !== currentYear) return null;
    const m = new Date().getMonth() + 1;
    return metrics.find((row) => row.month === m) || null;
  }, [metrics, year, currentYear]);

  const handleSaveFinances = async ({ finances, categorias }) => {
    setSaving(true);
    try {
      for (const f of finances) {
        const existing = financesData?.find(
          (d) => d.month === f.month && d.tipo === f.tipo && d.categoria === f.categoria
        );
        if (existing) {
          await supabase.from("monthly_finances").update({ importe: f.importe }).eq("id", existing.id);
        } else {
          await supabase.from("monthly_finances").insert({
            student_id: user.id,
            year,
            month: f.month,
            tipo: f.tipo,
            categoria: f.categoria,
            importe: f.importe,
          });
        }
      }

      const { data: existingConfig } = await supabase
        .from("metrics_config")
        .select("id")
        .eq("student_id", user.id)
        .single();

      const configUpdate = {
        categorias_ingresos: categorias.ingresos,
        categorias_gastos: categorias.gastos,
      };

      if (existingConfig) {
        await supabase.from("metrics_config").update(configUpdate).eq("id", existingConfig.id);
      } else {
        await supabase.from("metrics_config").insert({ student_id: user.id, ...configUpdate });
      }

      await Promise.all([refetchFinances(), refetchMonthly(), refetchConfig()]);
      showSuccess("Datos guardados correctamente");
      setShowFinancesPanel(false);
    } catch (err) {
      console.error(err);
      showError("No se pudieron guardar los datos");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async (data) => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from("metrics_config")
        .select("id")
        .eq("student_id", user.id)
        .single();

      if (existing) {
        const { error } = await supabase.from("metrics_config").update(data).eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("metrics_config").insert({ student_id: user.id, ...data });
        if (error) throw error;
      }
      await refetchConfig();
      showSuccess("Configuración guardada");
      setShowConfig(false);
    } catch (err) {
      showError("No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="animate-fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6"
      >
        <ChevronLeft size={18} /> Volver a Herramientas
      </button>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
            <BarChart3 size={24} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Métricas</h2>
            <p className="text-sm text-gray-500">Análisis completo de tu centro</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <GlobalMetric
            label="Perm. Media"
            value={`${(totals.permanencia_media / 12).toFixed(1)} años`}
            icon={Clock}
            tooltip="Permanencia Media: tiempo medio que un cliente lleva activo. Se calcula como (1 / churn rate medio) / 2. Se divide entre 2 porque representa la media, no el total esperado."
          />
          <GlobalMetric
            label="Ticket Medio"
            value={`${totals.ticket_medio.toFixed(2)}€`}
            icon={Euro}
            tooltip="Ticket Medio mensual por cliente. Se calcula como: Ingresos totales del año / Nº de clientes / 12 meses."
          />
          <GlobalMetric
            label="LTV"
            value={`${totals.ltv.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€`}
            icon={Euro}
            tooltip="Lifetime Value: ingreso medio que genera un cliente durante su permanencia. Se calcula como: Ticket Medio mensual × Permanencia Media (en meses)."
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white"
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFinancesPanel(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-brand rounded-lg hover:bg-brand-dark"
          >
            <Edit2 size={16} />
            Editar Datos
          </button>
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Settings size={16} />
            Objetivos
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPICard label="Clientes" value={totals.n_clientes} objetivo={config.capacidad_objetivo} icon={Users} />
        <KPICard label="% Capacidad" value={totals.pct_capacidad} objetivo={config.capacidad_pct_objetivo} format="percent" icon={Target} />
        <KPICard
          label="Leads"
          value={totals.leads}
          objetivo={config.leads_objetivo}
          icon={TrendingUp}
          currentMonthValue={currentMonthMetric?.leads}
        />
        <KPICard
          label="Altas"
          value={totals.altas}
          objetivo={config.altas_objetivo}
          icon={TrendingUp}
          currentMonthValue={currentMonthMetric?.altas}
        />
        <KPICard label="Tasa Cierre" value={totals.tasa_cierre} objetivo={config.tasa_cierre_objetivo} format="percent" />
        <KPICard label="Tasa Adquis." value={totals.tasa_adquis} objetivo={config.tasa_adquis_objetivo} format="percent" />
        <KPICard
          label="Bajas"
          value={totals.bajas}
          objetivo={config.bajas_objetivo}
          inverse={true}
          icon={TrendingDown}
          subline={`Reg: ${totals.bajas_regis}`}
          currentMonthValue={currentMonthMetric?.bajas}
        />
        <KPICard label="Churn Rate" value={totals.churn_rate} objetivo={config.churn_objetivo} format="percent" inverse={true} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Nº Clientes</h4>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={metrics} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorClientes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1B365D" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#1B365D" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value) => [value, "Clientes"]}
              />
              <Area type="monotone" dataKey="n_clientes" stroke="#1B365D" strokeWidth={2} fill="url(#colorClientes)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Altas / Bajas</h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={metrics} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value, name) => [value, name === "altas" ? "Altas" : "Bajas"]}
              />
              <ReferenceLine y={0} stroke="#e5e7eb" />
              <Bar dataKey="altas" fill="#10B981" radius={[4, 4, 0, 0]} name="altas" />
              <Bar dataKey="bajas" fill="#EF4444" radius={[4, 4, 0, 0]} name="bajas">
                {metrics.map((_, index) => (
                  <Cell key={`cell-${index}`} fill="#EF4444" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs text-emerald-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Altas
            </span>
            <span className="flex items-center gap-1 text-xs text-red-500">
              <span className="w-2 h-2 rounded-full bg-red-500"></span> Bajas
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Leads por mes</h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={metrics} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value, name) => [value, name === "leads" ? "Total Leads" : "Convertidos"]}
              />
              <Bar dataKey="leads" fill="#94a3b8" radius={[4, 4, 0, 0]} name="leads" />
              <Bar dataKey="altas" fill="#10B981" radius={[4, 4, 0, 0]} name="altas" />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span> Leads
            </span>
            <span className="flex items-center gap-1 text-xs text-emerald-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Convertidos
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Tasa de Cierre vs Churn</h4>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={metrics} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis 
                tick={{ fontSize: 10 }} 
                tickLine={false} 
                axisLine={false}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value) => [`${(value * 100).toFixed(1)}%`]}
              />
              <Line 
                type="monotone" 
                dataKey="tasa_cierre" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: "#3B82F6", strokeWidth: 0, r: 3 }}
                name="Tasa Cierre"
              />
              <Line 
                type="monotone" 
                dataKey="churn_rate" 
                stroke="#EF4444" 
                strokeWidth={2}
                dot={{ fill: "#EF4444", strokeWidth: 0, r: 3 }}
                name="Churn"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs text-blue-500">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span> Tasa Cierre
            </span>
            <span className="flex items-center gap-1 text-xs text-red-500">
              <span className="w-2 h-2 rounded-full bg-red-500"></span> Churn
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Ingresos / Gastos / Resultado</h4>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={metrics} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <YAxis 
              tick={{ fontSize: 10 }} 
              tickLine={false} 
              axisLine={false}
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) => [`${value.toLocaleString("es-ES")}€`]}
            />
            <ReferenceLine y={0} stroke="#e5e7eb" />
            <Bar dataKey="ingresos" fill="#10B981" radius={[4, 4, 0, 0]} name="Ingresos" />
            <Bar dataKey="gastos" fill="#EF4444" radius={[4, 4, 0, 0]} name="Gastos" />
            <Line 
              type="monotone" 
              dataKey="resultado" 
              stroke="#1B365D" 
              strokeWidth={2}
              dot={{ fill: "#1B365D", strokeWidth: 0, r: 3 }}
              name="Resultado"
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-4 mt-2">
          <span className="flex items-center gap-1 text-xs text-emerald-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Ingresos
          </span>
          <span className="flex items-center gap-1 text-xs text-red-500">
            <span className="w-2 h-2 rounded-full bg-red-500"></span> Gastos
          </span>
          <span className="flex items-center gap-1 text-xs text-brand">
            <span className="w-2 h-2 rounded-full bg-brand"></span> Resultado
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">% Cumplimiento KPIs</h4>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={kpiProgress} layout="horizontal" margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} domain={[0, 150]} />
            <Tooltip 
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) => [`${value.toFixed(0)}%`, "Cumplimiento"]}
            />
            <ReferenceLine y={100} stroke="#10B981" strokeDasharray="3 3" label={{ value: "100%", fontSize: 10, fill: "#10B981" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {kpiProgress.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.value >= 100 ? "#10B981" : "#F59E0B"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">Resumen Financiero {year}</h3>
          <button
            onClick={() => setShowFinances(!showFinances)}
            className="text-xs text-brand hover:underline flex items-center gap-1"
          >
            {showFinances ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showFinances ? "Ocultar" : "Ver detalle"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <span className="text-xs text-gray-500 block">Ingresos</span>
            <span className="text-lg font-bold text-emerald-600">
              {totals.ingresos.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
            </span>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <span className="text-xs text-gray-500 block">IVA Rep.</span>
            <span className="text-lg font-bold text-amber-600">
              {totals.iva.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <span className="text-xs text-gray-500 block">Gastos</span>
            <span className="text-lg font-bold text-red-600">
              {totals.gastos.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
            </span>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <span className="text-xs text-gray-500 block">IVA Sop.</span>
            <span className="text-lg font-bold text-orange-500">
              {totals.iva_soportado.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className={`text-center p-3 rounded-lg col-span-2 sm:col-span-1 ${totals.resultado >= 0 ? "bg-brand-light" : "bg-orange-50"}`}>
            <span className="text-xs text-gray-500 block">Resultado</span>
            <span className={`text-lg font-bold ${totals.resultado >= 0 ? "text-brand" : "text-orange-600"}`}>
              {totals.resultado.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
            </span>
          </div>
        </div>

        {showFinances && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left py-2">Mes</th>
                    <th className="text-right py-2 text-emerald-600">Ingresos</th>
                    <th className="text-right py-2 text-amber-600">IVA Rep.</th>
                    <th className="text-right py-2 text-red-500">Gastos</th>
                    <th className="text-right py-2 text-orange-500">IVA Sop.</th>
                    <th className="text-right py-2">Resultado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {metrics.map((m) => (
                    <tr key={m.month}>
                      <td className="py-2 font-medium">{m.label}</td>
                      <td className="py-2 text-right text-emerald-600">
                        {m.ingresos.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                      </td>
                      <td className="py-2 text-right text-amber-600">
                        {m.iva.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-2 text-right text-red-500">
                        {m.gastos.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                      </td>
                      <td className="py-2 text-right text-orange-500">
                        {m.iva_soportado.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
                      </td>
                      <td className={`py-2 text-right font-medium ${m.resultado >= 0 ? "text-brand" : "text-orange-600"}`}>
                        {m.resultado.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {(() => {
        const QUARTERS = [
          { label: "T1", months: [1, 2, 3] },
          { label: "T2", months: [4, 5, 6] },
          { label: "T3", months: [7, 8, 9] },
          { label: "T4", months: [10, 11, 12] },
        ];
        const quarterData = QUARTERS.map(q => {
          const qMetrics = metrics.filter(m => q.months.includes(m.month));
          const rep = qMetrics.reduce((s, m) => s + m.iva, 0);
          const sop = qMetrics.reduce((s, m) => s + m.iva_soportado, 0);
          return { label: q.label, rep, sop, liquidacion: rep - sop };
        });
        const totalRep = quarterData.reduce((s, q) => s + q.rep, 0);
        const totalSop = quarterData.reduce((s, q) => s + q.sop, 0);
        const totalLiq = totalRep - totalSop;
        const fmt = (v) => v.toLocaleString("es-ES", { maximumFractionDigits: 0 });

        return (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Liquidación IVA Trimestral {year}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="text-left py-2 font-medium">Trimestre</th>
                    <th className="text-right py-2 font-medium text-amber-600">IVA Rep.</th>
                    <th className="text-right py-2 font-medium text-orange-500">IVA Sop.</th>
                    <th className="text-right py-2 font-medium">Liquidación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {quarterData.map(q => (
                    <tr key={q.label}>
                      <td className="py-2.5 font-medium text-gray-700">{q.label}</td>
                      <td className="py-2.5 text-right text-amber-600">{fmt(q.rep)}€</td>
                      <td className="py-2.5 text-right text-orange-500">{fmt(q.sop)}€</td>
                      <td className={`py-2.5 text-right font-semibold ${q.liquidacion >= 0 ? "text-red-500" : "text-emerald-600"}`}>
                        {fmt(q.liquidacion)}€
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="py-2.5 font-semibold text-gray-800">TOTAL</td>
                    <td className="py-2.5 text-right font-semibold text-amber-600">{fmt(totalRep)}€</td>
                    <td className="py-2.5 text-right font-semibold text-orange-500">{fmt(totalSop)}€</td>
                    <td className={`py-2.5 text-right font-bold ${totalLiq >= 0 ? "text-red-500" : "text-emerald-600"}`}>
                      {fmt(totalLiq)}€
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Liquidación positiva = a pagar a Hacienda · Negativa = a compensar/devolver
            </p>
          </div>
        );
      })()}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Detalle Mensual {year}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-3 py-3 font-medium text-gray-600">Mes</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">Clientes</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">%Cap</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">Leads</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">Altas</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right text-amber-600">L.Espera</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">T.Cierre</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">T.Adq.</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">Bajas</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right text-gray-400">B.Reg</th>
                <th className="px-3 py-3 font-medium text-gray-600 text-right">Churn</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {metrics.map((m, idx) => (
                <tr
                  key={m.month}
                  className={`transition-colors ${idx % 2 === 1 ? "bg-slate-100" : "bg-white"} hover:bg-blue-50`}
                >
                  <td className="px-3 py-2.5 font-medium text-gray-900">{m.label}</td>
                  <td className="px-3 py-2.5 text-right">{m.n_clientes}</td>
                  <td className="px-3 py-2.5 text-right">{(m.pct_capacidad * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2.5 text-right">{m.leads}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">{m.altas}</td>
                  <td className="px-3 py-2.5 text-right text-amber-600">{m.lista_espera}</td>
                  <td className="px-3 py-2.5 text-right">{(m.tasa_cierre * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2.5 text-right">{(m.tasa_adquis * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-right text-red-500 font-medium">{m.bajas}</td>
                  <td className="px-3 py-2.5 text-right text-gray-400">{m.bajas_regis}</td>
                  <td className="px-3 py-2.5 text-right">{(m.churn_rate * 100).toFixed(1)}%</td>
                </tr>
              ))}
              <tr className="bg-brand-light font-semibold">
                <td className="px-3 py-2.5 text-brand">TOTAL</td>
                <td className="px-3 py-2.5 text-right text-brand">{totals.n_clientes}</td>
                <td className="px-3 py-2.5 text-right text-brand">{(totals.pct_capacidad * 100).toFixed(0)}%</td>
                <td className="px-3 py-2.5 text-right text-brand">{totals.leads}</td>
                <td className="px-3 py-2.5 text-right text-emerald-600">{totals.altas}</td>
                <td className="px-3 py-2.5 text-right text-amber-600">{totals.lista_espera}</td>
                <td className="px-3 py-2.5 text-right text-brand">{(totals.tasa_cierre * 100).toFixed(0)}%</td>
                <td className="px-3 py-2.5 text-right text-brand">{(totals.tasa_adquis * 100).toFixed(1)}%</td>
                <td className="px-3 py-2.5 text-right text-red-500">{totals.bajas}</td>
                <td className="px-3 py-2.5 text-right text-gray-400">{totals.bajas_regis}</td>
                <td className="px-3 py-2.5 text-right text-brand">{(totals.churn_rate * 100).toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {showFinancesPanel && (
        <FinancesPanel
          year={year}
          config={config}
          finances={financesData || []}
          onSave={handleSaveFinances}
          onClose={() => setShowFinancesPanel(false)}
          saving={saving}
        />
      )}

      {showConfig && (
        <ConfigModal
          config={config}
          onSave={handleSaveConfig}
          onClose={() => setShowConfig(false)}
          saving={saving}
        />
      )}
    </div>
  );
}
